import {Undef} from 'app/interface';
import {hasProp} from 'app/utils/hasProp';
import {getClientPlaceholderValues} from 'app/utils/replacePlaceholder/getClientPlaceholderValues';
import {MustacheTags} from 'app/utils/template/interface';

import {GemSettings} from '../config/interface';

export function renderPlaceholder(templateRender: TemplateRender) {
    return ({template, gemSettings, additionalValues = {}, skipStorage = false, tags, hydrated}: RenderPlaceholderParams) => {
        const values = getClientPlaceholderValues(gemSettings, hydrated);
        let valuesToReplace: TemplateAdditionalValues;
        if (typeof additionalValues === 'string') {
            valuesToReplace = additionalValues;
        } else {
            valuesToReplace = {
                ...values,
                ...additionalValues,
            };
        }

        if (skipStorage && hasProp('storage', valuesToReplace)) {
            valuesToReplace = getValuesToReplaceWithSafeStorage(valuesToReplace);
        }

        const internalString = replacePlaceholderEncodedHref(templateRender, template, valuesToReplace, gemSettings);

        return templateRender(internalString, valuesToReplace, gemSettings, tags);
    };
}

function replacePlaceholderEncodedHref(
    fn: (templateInput: string, values: TemplateAdditionalValues, gemSettings: GemSettings, tags?: [string, string]) => string,
    str: string,
    valuesToReplace: TemplateAdditionalValues,
    gemSettings: GemSettings,
): string {
    const hrefPattern = /<a[^>]*href=["']([^"']*)["']/g;
    if (hrefPattern.test(str)) {
        return str.replace(hrefPattern, (match) => {
            const tags: [string, string] = ['%7B%7B', '%7D%7D']; // html encoded {{ }}
            return fn(match, valuesToReplace, gemSettings, tags);
        });
    }

    return str;
}

function getValuesToReplaceWithSafeStorage(valuesToReplace: TemplateAdditionalValues): TemplateAdditionalValues {
    if (!hasProp('storage', valuesToReplace)) {
        return valuesToReplace;
    }

    const valuesWithStorage = valuesToReplace as Exclude<TemplateAdditionalValues, string> & {storage: unknown};
    const safeStorageValue = getSafeStorageValue(valuesWithStorage.storage);

    if (!safeStorageValue) {
        const {storage: _storage, ...valuesWithoutStorage} = valuesWithStorage;
        return valuesWithoutStorage;
    }

    return {
        ...valuesWithStorage,
        storage: safeStorageValue,
    };
}

function getSafeStorageValue(storageValue: unknown): Record<string, string> | undefined {
    let safeStorageValue: Record<string, string> = {};

    SAFE_STORAGE_FIELDS.forEach((field) => {
        if (hasProp<Record<string, unknown>>(field, storageValue) && typeof storageValue[field] === 'string') {
            safeStorageValue = {
                ...safeStorageValue,
                [field]: storageValue[field],
            };
        }
    });

    if (Object.keys(safeStorageValue).length === 0) {
        return;
    }

    return safeStorageValue;
}

const SAFE_STORAGE_FIELDS = ['urlFragment'] as const;

type TemplateRender = (templateInput: string, values: TemplateAdditionalValues, gemSettings: GemSettings, tags?: MustacheTags) => string;
export type TemplateAdditionalValues = Record<string, unknown> | string;

type RenderPlaceholderParams = {
    template: string;
    gemSettings: GemSettings;
    additionalValues?: TemplateAdditionalValues;
    skipStorage?: boolean;
    tags?: Undef<MustacheTags>;
    hydrated?: boolean;
};
